import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FileText, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { Snippet } from '@/components/common/Snippet';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchDocumentTypes, searchDocuments } from '@/services/anatel';
import type { DocumentFilters, DocumentHit } from '@/services/types';

const PAGE_SIZE = 25;

/**
 * Search the raw document corpus: 2.46M documents, text only.
 *
 * The widest reach in the product and the shallowest. There is no outcome here,
 * no sanction, no rapporteur and no extraction of any kind — this answers "where
 * does the agency say this", and nothing about how anything was decided.
 *
 * Two limits are structural and both are shown on the face of the screen rather
 * than in a footnote, because each one produces a result that looks like a
 * successful search:
 *
 * 1. The index stores ACCENTED forms. "prescrição" matches 32,299 documents and
 *    "prescricao" matches 3 (measured 2026-08-10; see the dated note in
 *    `repositories/anatel_documents.py`). Three results is not a smaller
 *    answer to the same question, it is a different question.
 * 2. Above the candidate window the ordering is relevance-within-a-window, not
 *    across the corpus. The total stays exact; the ranking is what is capped.
 */
export function AnatelDocumentsPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [draft, setDraft] = useState('');
  const [filters, setFilters] = useState<DocumentFilters>({
    query: null,
    limit: PAGE_SIZE,
    offset: 0,
  });

  const { data: types = [] } = useQuery({
    queryKey: ['anatel', 'document-types'],
    queryFn: fetchDocumentTypes,
    staleTime: Infinity,
  });

  const { data, isPending, error, refetch, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['anatel', 'documents', filters],
    queryFn: () => searchDocuments(filters),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const offset = filters.offset ?? 0;
  const update = (next: Partial<DocumentFilters>) =>
    setFilters((current) => ({ ...current, ...next, offset: 0 }));

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:documents.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:documents.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:documents.explainer" />

      <FieldGuide i18nKey="anatel:documents.guide" level={2} />

      <div className="flex justify-end">
        {/* Disabled with no query, because an unfiltered export of 2.46M
            documents is not a result set anyone asked for. */}
        <ExportButton path="/api/v1/anatel/export/documents" body={filters} disabled={!filters.query} />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          update({ query: draft.trim() || null });
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="document-query" className="text-xs font-medium text-foreground">
            {t('anatel:documents.search.label')}
          </label>
          <input
            id="document-query"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('anatel:documents.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
          <p className="text-xs text-muted-foreground">{t('anatel:documents.search.hint')}</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="document-type" className="text-xs font-medium text-foreground">
            {t('anatel:documents.filters.type')}
          </label>
          <select
            id="document-type"
            value={filters.document_type ?? ''}
            onChange={(event) => update({ document_type: event.target.value || null })}
            className="max-w-[14rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('anatel:documents.filters.typeAny')}</option>
            {types.map((option) => (
              <option key={option.document_type} value={option.document_type}>
                {option.document_type} ({number.format(option.documents)})
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 py-1.5 text-sm text-foreground">
          <input
            type="checkbox"
            checked={filters.linkable_only ?? false}
            onChange={(event) => update({ linkable_only: event.target.checked })}
            className="h-4 w-4 rounded border"
          />
          {t('anatel:documents.filters.linkableOnly')}
        </label>

        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('anatel:documents.search.submit')}
        </button>
      </form>

      {isPending ? (
        <SkeletonLoader rows={6} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-3">
          {/* The accent warning sits ABOVE the count, not below it. Below, the
              reader has already read "3 results" as an answer. */}
          {data.search.value?.query_unaccented ? (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{t('anatel:documents.accentWarning')}</span>
            </p>
          ) : null}

          <Answer answer={data.search} help={t('anatel:documents.help')}>
            {(summary) => (
              <div className="rounded-lg border bg-card p-3">
                {/*
                  `data-stale` is what makes this number readable by anything
                  other than a human.

                  `keepPreviousData` means the PREVIOUS query's total stays on
                  screen for the whole of the next fetch -- deliberately, so the
                  figure does not blink -- and the rendered sentence gives no way
                  to tell which query it describes. A reader who narrows the
                  filters and sees the old number cannot distinguish "the filter
                  changed nothing" from "the filter has not landed yet", and
                  neither could a test: it read the stale total and reported the
                  filter as a no-op. `isPlaceholderData` is exactly the fact
                  needed -- true while the shown data belongs to a different
                  query key -- and `data-total` carries the raw integer so
                  nothing has to parse digits back out of a localised sentence.
                */}
                <p
                  className="text-sm text-foreground"
                  aria-live="polite"
                  data-testid="documents-total"
                  data-total={summary.total}
                  data-stale={String(isPlaceholderData)}
                >
                  {t('anatel:documents.summary', { total: number.format(summary.total) })}
                  {isFetching ? ` · ${t('common:state.loading')}` : ''}
                </p>
                {summary.ranking_windowed ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('anatel:documents.windowed', {
                      window: number.format(summary.window),
                      total: number.format(summary.total),
                    })}
                  </p>
                ) : null}
              </div>
            )}
          </Answer>

          {data.search.value?.no_searchable_terms ? (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground">
              {t('anatel:documents.noSearchableTerms')}
            </p>
          ) : data.items.length === 0 ? (
            <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              {t('anatel:documents.noMatches')}
            </p>
          ) : (
            <>
              <ul className="space-y-3">
                {data.items.map((hit, index) => (
                  <DocumentCard key={`${hit.source_file}-${index}`} hit={hit} />
                ))}
              </ul>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  disabled={offset === 0}
                  onClick={() =>
                    setFilters({ ...filters, offset: Math.max(offset - PAGE_SIZE, 0) })
                  }
                  className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {t('anatel:decisions.previous')}
                </button>
                <button
                  type="button"
                  disabled={offset + PAGE_SIZE >= (data.search.value?.total ?? 0)}
                  onClick={() => setFilters({ ...filters, offset: offset + PAGE_SIZE })}
                  className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {t('anatel:decisions.next')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DocumentCard({ hit }: { hit: DocumentHit }) {
  const { t } = useTranslation(['anatel', 'common']);

  return (
    <li className="space-y-2 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {hit.document_type ? (
          <span className="font-medium text-foreground">{hit.document_type}</span>
        ) : null}
        {hit.tematica ? <span>{hit.tematica}</span> : null}
        {hit.reference_date_raw ? <span>{hit.reference_date_raw}</span> : null}
        {hit.entity_uf ? <span>{hit.entity_uf}</span> : null}
      </div>

      <p className="text-sm text-foreground">
        <Snippet text={hit.snippet} />
      </p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2 text-xs text-muted-foreground">
        {/* Linked ONLY when the process exists here. 78.8% of documents carry a
            process number but 7.7% name one this application can open, and a
            link that 404s is worse than no link. */}
        {hit.process_key ? (
          <Link
            to={`/anatel/p/${hit.process_key}`}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {hit.process_number}
          </Link>
        ) : hit.process_number ? (
          <span title={t('anatel:documents.notAnalysed')}>
            {hit.process_number} · {t('anatel:documents.notAnalysedShort')}
          </span>
        ) : (
          <span className="italic">{t('anatel:documents.noProcess')}</span>
        )}
        <span className="ml-auto max-w-xs truncate" title={hit.source_file}>
          {hit.source_file}
        </span>
      </div>
    </li>
  );
}
